import React from "react";
import { motion } from "framer-motion";
import { useCountUp } from "@/hooks/useCountUp";
import statsImage from "@/assets/stats-image-v2.webp";

interface StatItemProps {
  value: number;
  suffix?: string;
  description: string;
  delay?: number;
}

function StatItem({ value, suffix = "%", description, delay = 0 }: StatItemProps) {
  const { count, ref } = useCountUp({ end: value, duration: 2000 });

  return (
    <motion.div
      ref={ref}
      className="flex items-start gap-8 border-l-[8px] border-beacon pl-6"
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, ease: "easeOut", delay }}
    >
      <span className="text-h2 text-foreground min-w-[120px]">
        {count}
        {suffix}
      </span>
      <p className="text-body-lg text-foreground">{description}</p>
    </motion.div>
  );
}

const stats = [
  {
    value: 83,
    description: "of seniors felt GrandPad was easy to use and helped them connect to healthcare providers.",
  },
  {
    value: 85,
    description: "of users report improved mood, health, and overall outlook since using GrandPad.",
  },
  {
    value: 99,
    description: "of patients completed digital consent forms with GrandPad-enabled workflows.",
  },
  {
    value: 100,
    description: "of providers who use GrandPad say it is easy to use and efficient.",
  },
];

export function StatsSection() {
  return (
    <section className="w-full bg-sailcloth overflow-hidden" aria-labelledby="stats-heading">
      <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-8 md:px-12 lg:px-20 3xl:px-28 4xl:px-32">
        <div className="flex gap-16 max-lg:flex-col max-lg:gap-12">
          {/* Left column - Content */}
          <motion.div
            className="flex-1 flex flex-col gap-12 py-24 max-md:py-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div>
              <span className="text-caption text-beacon uppercase mb-4 block pl-[3px]">By The Numbers</span>
              <h2 id="stats-heading" className="text-h1 text-foreground">
                Proven Results for Patients and Providers
              </h2>
            </div>

            <div className="flex flex-col gap-10">
              {stats.map((stat, index) => (
                <StatItem key={index} value={stat.value} description={stat.description} delay={index * 0.1} />
              ))}
            </div>
          </motion.div>

          {/* Right column - Image */}
          <motion.div
            className="flex-1 flex items-center justify-center max-lg:pt-0 max-lg:pb-8"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <img
              src={statsImage}
              alt="Happy older adult using GrandPad tablet"
              className="w-full max-w-[500px] h-auto"
              width={1200}
              height={1200}
              loading="lazy"
              decoding="async"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
